import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiSave,
  FiX,
  FiUpload,
  FiTrash2,
  FiArrowLeft
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminApartmentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Studio',
    location: '',
    address: {
      street: '',
      city: '',
      postcode: ''
    },
    pricePerNight: '',
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    maxGuests: 2,
    amenities: [],
    checkInTime: '15:00',
    checkOutTime: '11:00',
    isActive: true,
    featured: false
  });

  const apartmentTypes = ['Studio', '1-Bed', '2-Bed', '3-Bed', 'Penthouse'];
  const amenitiesList = [
    'WiFi', 'Air Conditioning', 'Heating', 'Kitchen', 'Washer', 'Dryer',
    'TV', 'Parking', 'Gym', 'Pool', 'Elevator', 'Workspace',
    'Iron', 'Hair Dryer', 'Coffee Maker', 'Dishwasher', 'Balcony', 'Garden'
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locData = await api.getLocations();
        setLocations(Array.isArray(locData) ? locData : (locData.locations || []));

        if (isEditing) {
          const aptData = await api.getApartmentById(id);
          setFormData({
            name: aptData.name || '',
            description: aptData.description || '',
            type: aptData.type || 'Studio',
            location: aptData.location?._id || '',
            address: aptData.address || { street: '', city: '', postcode: '' },
            pricePerNight: aptData.pricePerNight || '',
            bedrooms: aptData.bedrooms || 1,
            bathrooms: aptData.bathrooms || 1,
            beds: aptData.beds || 1,
            maxGuests: aptData.maxGuests || 2,
            amenities: aptData.amenities || [],
            checkInTime: aptData.checkInTime || '15:00',
            checkOutTime: aptData.checkOutTime || '11:00',
            isActive: aptData.isActive !== false,
            featured: aptData.featured || false
          });
          setImages(aptData.images || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error loading data');
      }
    };
    fetchData();
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('images', file));

    try {
      const data = await api.uploadApartmentImages(formDataUpload);
      setImages(prev => [...prev, ...data.urls]);
      toast.success('Images uploaded');
    } catch (error) {
      toast.error('Failed to upload images');
    }
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData, images };

      if (isEditing) {
        await api.updateApartment(id, payload);
        toast.success('Apartment updated successfully');
      } else {
        await api.createApartment(payload);
        toast.success('Apartment created successfully');
      }
      navigate('/admin/apartments');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '24px'
    },
    backBtn: {
      padding: '8px',
      background: 'none',
      border: '1px solid #ddd',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    form: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: '24px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      marginBottom: '24px'
    },
    cardTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      marginBottom: '20px'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontWeight: '500',
      marginBottom: '8px',
      fontSize: '0.875rem'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem',
      minHeight: '120px',
      resize: 'vertical'
    },
    select: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem',
      background: 'white'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px'
    },
    amenitiesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '10px'
    },
    amenityBtn: {
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer',
      fontSize: '0.8125rem',
      transition: 'all 0.2s'
    },
    amenityActive: {
      background: '#1a3a5c',
      color: 'white',
      borderColor: '#1a3a5c'
    },
    imageUpload: {
      border: '2px dashed #ddd',
      borderRadius: '8px',
      padding: '24px',
      textAlign: 'center',
      cursor: 'pointer'
    },
    imagePreviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
      marginTop: '16px'
    },
    imagePreview: {
      position: 'relative',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    previewImg: {
      width: '100%',
      height: '80px',
      objectFit: 'cover'
    },
    removeBtn: {
      position: 'absolute',
      top: '4px',
      right: '4px',
      padding: '4px',
      background: 'rgba(0,0,0,0.6)',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px'
    },
    actions: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px'
    },
    btn: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    btnPrimary: {
      background: '#1a3a5c',
      color: 'white',
      border: 'none'
    },
    btnSecondary: {
      background: 'white',
      color: '#222',
      border: '1px solid #ddd'
    }
  };

  return (
    <div>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/admin/apartments')}>
          <FiArrowLeft />
        </button>
        <h1 style={styles.title}>{isEditing ? 'Edit Apartment' : 'New Apartment'}</h1>
      </header>

      <form onSubmit={handleSubmit}>
        <div style={styles.form} className="apartment-form-grid">
          {/* Main Content */}
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Basic Information</h3>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Apartment Name *</label>
                <input
                  type="text"
                  name="name"
                  style={styles.input}
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Description *</label>
                <textarea
                  name="description"
                  style={styles.textarea}
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Type</label>
                  <select
                    name="type"
                    style={styles.select}
                    value={formData.type}
                    onChange={handleChange}
                  >
                    {apartmentTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Location</label>
                  <select
                    name="location"
                    style={styles.select}
                    value={formData.location}
                    onChange={handleChange}
                  >
                    <option value="">Select location</option>
                    {locations.map(loc => (
                      <option key={loc._id} value={loc._id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Address</h3>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Street</label>
                <input
                  type="text"
                  name="address.street"
                  style={styles.input}
                  value={formData.address.street}
                  onChange={handleChange}
                />
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    name="address.city"
                    style={styles.input}
                    value={formData.address.city}
                    onChange={handleChange}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Postcode</label>
                  <input
                    type="text"
                    name="address.postcode"
                    style={styles.input}
                    value={formData.address.postcode}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Details</h3>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Price per Night (£) *</label>
                  <input
                    type="number"
                    name="pricePerNight"
                    style={styles.input}
                    value={formData.pricePerNight}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Number of Beds *</label>
                  <input
                    type="number"
                    name="beds"
                    style={styles.input}
                    value={formData.beds}
                    onChange={handleChange}
                    min="1"
                    required
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Bedrooms</label>
                  <input
                    type="number"
                    name="bedrooms"
                    style={styles.input}
                    value={formData.bedrooms}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Bathrooms</label>
                  <input
                    type="number"
                    name="bathrooms"
                    style={styles.input}
                    value={formData.bathrooms}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Max Guests</label>
                  <input
                    type="number"
                    name="maxGuests"
                    style={styles.input}
                    value={formData.maxGuests}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Check-in Time</label>
                  <input
                    type="time"
                    name="checkInTime"
                    style={styles.input}
                    value={formData.checkInTime}
                    onChange={handleChange}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Check-out Time</label>
                  <input
                    type="time"
                    name="checkOutTime"
                    style={styles.input}
                    value={formData.checkOutTime}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Amenities</h3>
              <div style={styles.amenitiesGrid}>
                {amenitiesList.map(amenity => (
                  <button
                    key={amenity}
                    type="button"
                    style={{
                      ...styles.amenityBtn,
                      ...(formData.amenities.includes(amenity) ? styles.amenityActive : {})
                    }}
                    onClick={() => handleAmenityToggle(amenity)}
                  >
                    {amenity}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Images</h3>
              <label style={styles.imageUpload}>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <FiUpload size={24} style={{ marginBottom: '8px' }} />
                <div>Click to upload images</div>
                <small style={{ color: '#555' }}>JPG, PNG up to 5MB</small>
              </label>

              {images.length > 0 && (
                <div style={styles.imagePreviewGrid}>
                  {images.map((img, index) => (
                    <div key={index} style={styles.imagePreview}>
                      <img src={img} alt={`Preview ${index}`} style={styles.previewImg} />
                      <button
                        type="button"
                        style={styles.removeBtn}
                        onClick={() => removeImage(index)}
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Status</h3>

              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                Active (visible on site)
              </label>

              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                />
                Featured on homepage
              </label>
            </div>

            <div style={styles.actions}>
              <button
                type="submit"
                style={{ ...styles.btn, ...styles.btnPrimary }}
                disabled={loading}
              >
                <FiSave /> {loading ? 'Saving...' : 'Save Apartment'}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnSecondary }}
                onClick={() => navigate('/admin/apartments')}
              >
                <FiX /> Cancel
              </button>
            </div>
          </div>
        </div>
      </form>

      <style>{`
        @media (max-width: 1024px) {
          .apartment-form-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminApartmentForm;
